import { useState, useRef, useEffect } from "react";
import { Sparkles, X, Send, Loader2 } from "lucide-react";
import { API_BASE } from "@/lib/api";

function getSessionId() {
  let id = localStorage.getItem("cc_ai_session");
  if (!id) {
    id = "ai_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem("cc_ai_session", id);
  }
  return id;
}

export default function AIAssistant() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi, I'm Sparkle ✨ your My Clean Connect™ assistant. I can help you pick a service, estimate prices, or find the perfect cleaner. What do you need today?" },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);
  const sessionId = useRef(getSessionId());

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ session_id: sessionId.current, message: text }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: acc };
          return copy;
        });
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry, I couldn't connect right now. Please try again." };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(!open)}
        className="fixed bottom-5 right-5 z-50 h-14 w-14 rounded-full bg-accent text-white shadow-[0_10px_30px_rgba(180,107,71,0.4)] flex items-center justify-center hover:scale-105 transition-transform"
        data-testid="ai-assistant-toggle"
        aria-label="Open AI assistant"
      >
        {open ? <X className="h-6 w-6" /> : <Sparkles className="h-6 w-6" />}
      </button>

      {open && (
        <div className="fixed bottom-24 right-5 z-50 w-[calc(100vw-2.5rem)] sm:w-[400px] h-[560px] max-h-[75vh] bg-card rounded-3xl shadow-2xl border border-border flex flex-col overflow-hidden" data-testid="ai-assistant-panel">
          <div className="bg-primary text-primary-foreground px-5 py-4 flex items-center gap-3">
            <span className="h-9 w-9 rounded-full bg-accent flex items-center justify-center">
              <Sparkles className="h-5 w-5 text-white" />
            </span>
            <div>
              <p className="font-display font-semibold leading-tight">Sparkle AI</p>
              <p className="text-xs text-primary-foreground/60">Your cleaning concierge</p>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-primary text-primary-foreground rounded-br-sm" : "bg-secondary text-secondary-foreground rounded-bl-sm"}`} data-testid={`ai-msg-${m.role}`}>
                  {m.content || (busy && i === messages.length - 1 ? <Loader2 className="h-4 w-4 animate-spin" /> : "")}
                </div>
              </div>
            ))}
          </div>

          <div className="p-3 border-t border-border flex items-center gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask me anything…"
              className="flex-1 px-4 py-2.5 rounded-full bg-background border border-border text-sm outline-none focus:border-accent transition-colors"
              data-testid="ai-input"
            />
            <button onClick={send} disabled={busy} className="h-10 w-10 rounded-full bg-accent text-white flex items-center justify-center disabled:opacity-50 hover:bg-accent/90 transition-colors" data-testid="ai-send">
              <Send className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
